<template>
    <div class="flex flex-col items-center gap-6 md:gap-10 w-full max-w-4xl">
        <div class="text-center leading-tight">
            <h2 class="max-w-2xl display-medium text-balance mb-4">
                <span class="text-ink">{{ t("onboarding.evaluator.experienceSelection.title") }}</span>
            </h2>
            <p class="text-ink-muted body md:display-serif max-w-2xl mx-auto text-pretty">{{ t("onboarding.evaluator.experienceSelection.subtitle") }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full">
            <BaseCard
                v-for="option in options"
                :key="option.id"
                as="button"
                type="button"
                class="group text-left"
                :class="[
                    tonalThemeEnabled ? 'focus-field' : 'focus-ring',
                    option.disabled ? 'opacity-50 cursor-not-allowed' : '',
                    selectedOption === option.id ? 'border-control-accent ring-2 ring-control-halo' : '',
                ]"
                :aria-pressed="selectedOption === option.id"
                :disabled="option.disabled"
                @click="!option.disabled && selectOption(option.id)">
                <div class="flex flex-col h-full gap-3">
                    <div class="flex items-center gap-3">
                        <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-sunken" :class="selectedOption === option.id ? 'text-ink' : 'text-ink-muted'">
                            <i :class="`bi ${option.icon} text-xl`" aria-hidden="true"></i>
                        </div>
                        <BaseChip v-if="option.badge" size="2xs" class="text-ink-muted">
                            {{ option.badge }}
                        </BaseChip>
                    </div>
                    <h3 class="title-body text-ink">{{ option.title }}</h3>
                    <p class="body-small text-ink-muted flex-1 text-pretty">{{ option.description }}</p>
                    <p class="details text-ink-muted mt-2">{{ option.detail }}</p>
                </div>
            </BaseCard>
        </div>

        <div class="flex items-center gap-4 mt-2">
            <BaseButton theme="tertiary" @click="emit('back')"> <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> {{ t("onboarding.evaluator.shared.back") }} </BaseButton>
            <BaseButton theme="primary" class="w-60" :disabled="!selectedOption" @click="handleContinue">
                {{ selectedOption ? t("onboarding.evaluator.experienceSelection.getStarted") : t("onboarding.evaluator.experienceSelection.selectAnOption") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

export type ExperienceOption = "preloaded" | "fresh" | "admin";

const emit = defineEmits<{
    select: [option: ExperienceOption];
    back: [];
}>();

const { t } = useI18n();

const selectedOption = ref<ExperienceOption | null>(null);

const options = computed(() => [
    {
        id: "preloaded" as const,
        icon: "bi-lightning-charge-fill",
        title: t("onboarding.evaluator.experienceSelection.options.preloaded.title"),
        description: t("onboarding.evaluator.experienceSelection.options.preloaded.description"),
        detail: t("onboarding.evaluator.experienceSelection.options.preloaded.detail"),
        badge: t("onboarding.evaluator.experienceSelection.options.preloaded.badge"),
        disabled: false,
    },
    {
        id: "fresh" as const,
        icon: "bi-person-plus",
        title: t("onboarding.evaluator.experienceSelection.options.fresh.title"),
        description: t("onboarding.evaluator.experienceSelection.options.fresh.description"),
        detail: t("onboarding.evaluator.experienceSelection.options.fresh.detail"),
        badge: null,
        disabled: false,
    },
    {
        id: "admin" as const,
        icon: "bi-building",
        title: t("onboarding.evaluator.experienceSelection.options.admin.title"),
        description: t("onboarding.evaluator.experienceSelection.options.admin.description"),
        detail: t("onboarding.evaluator.experienceSelection.options.admin.detail"),
        badge: t("onboarding.evaluator.experienceSelection.options.admin.badge"),
        disabled: true,
    },
]);

function selectOption(option: ExperienceOption) {
    selectedOption.value = option;
}

function handleContinue() {
    if (selectedOption.value) {
        emit("select", selectedOption.value);
    }
}
</script>
